import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { categories } from "@/mocks/categories";

export default function Navbar() {
  const { t } = useTranslation();
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileSubOpen, setMobileSubOpen] = useState(false);
  const mobileRef = useRef<HTMLDivElement>(null);
  const hamburgerRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 60);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        mobileRef.current &&
        !mobileRef.current.contains(target) &&
        hamburgerRef.current &&
        !hamburgerRef.current.contains(target)
      ) {
        setMobileMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleCategorySelect = (url: string) => {
    setMobileMenuOpen(false);
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const scrollToSection = (id: string) => {
    setMobileMenuOpen(false);
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: "smooth" });
    }
  };

  const linkClass = `text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
    scrolled ? "text-foreground-700 hover:text-foreground-950" : "text-white/80 hover:text-white"
  }`;

  const mobileLinkClass = "block w-full text-left px-4 py-3 text-sm font-medium text-foreground-800 hover:bg-primary-50 hover:text-primary-700 transition-colors cursor-pointer whitespace-nowrap";

  return (
    <nav
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${
        scrolled
          ? "bg-background-50/95 backdrop-blur-md border-b border-foreground-100/20"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-4 md:px-6 flex items-center justify-between h-[64px] md:h-[72px]">
        <Link
          to="/"
          className={`font-heading text-lg md:text-xl font-semibold whitespace-nowrap ${
            scrolled ? "text-foreground-900" : "text-white"
          }`}
        >
          OOPBUY Spreadsheet Guide
        </Link>

        <div className="hidden md:flex items-center gap-6">
          <Link to="/" className={linkClass}>
            <i className="ri-home-line inline mr-1 w-4 h-4 align-middle"></i>
            {t("nav_home")}
          </Link>
          <Link to="/einkaufsguide" className={linkClass}>
            <i className="ri-shopping-bag-line inline mr-1 w-4 h-4 align-middle"></i>
            Einkaufsguide
          </Link>
          <button onClick={() => scrollToSection("categories")} className={linkClass}>
            {t("nav_categories")}
          </button>
          <button onClick={() => scrollToSection("guides")} className={linkClass}>
            {t("nav_guides")}
          </button>
          <button onClick={() => scrollToSection("comparison")} className={linkClass}>
            {t("nav_comparison")}
          </button>
          <button onClick={() => scrollToSection("security")} className={linkClass}>
            {t("nav_security")}
          </button>
        </div>

        <div className="flex items-center gap-2 md:gap-3">
          <a
            href="https://kaspreadsheet.com"
            target="_blank"
            rel="noopener nofollow"
            className={`hidden sm:inline-flex px-4 py-2 text-sm font-semibold rounded-full transition-all cursor-pointer whitespace-nowrap ${
              scrolled
                ? "bg-primary-500 text-white hover:bg-primary-600"
                : "bg-primary-500/80 text-white hover:bg-primary-500 backdrop-blur-sm"
            }`}
          >
            {t("hero_cta")}
          </a>

          <a
            href="https://kaspreadsheet.com"
            target="_blank"
            rel="noopener nofollow"
            className={`hidden sm:inline-flex px-4 py-2 text-sm font-semibold rounded-full border transition-all cursor-pointer whitespace-nowrap ${
              scrolled
                ? "border-foreground-300 text-foreground-800 hover:bg-foreground-50"
                : "border-white/40 text-white hover:bg-white/15"
            }`}
          >
            {t("cta_button")}
          </a>

          <button
            ref={hamburgerRef}
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label={mobileMenuOpen ? "Menü schließen" : "Menü öffnen"}
            className={`md:hidden w-9 h-9 flex items-center justify-center rounded-lg cursor-pointer ${
              scrolled
                ? "text-foreground-800 hover:bg-background-100"
                : "text-white hover:bg-white/15"
            }`}
          >
            <i className={`${mobileMenuOpen ? "ri-close-line" : "ri-menu-line"} text-xl w-5 h-5 flex items-center justify-center`}></i>
          </button>
        </div>
      </div>

      {mobileMenuOpen && (
        <div ref={mobileRef} className="md:hidden bg-background-50 border-t border-foreground-100/20 max-h-[80vh] overflow-y-auto">
          <div className="px-2 py-2">
            <Link to="/" className={mobileLinkClass} onClick={() => setMobileMenuOpen(false)}>
              <i className="ri-home-line inline mr-2 w-4 h-4 align-middle"></i>
              {t("nav_home")}
            </Link>
            <Link to="/einkaufsguide" className={mobileLinkClass} onClick={() => setMobileMenuOpen(false)}>
              <i className="ri-shopping-bag-line inline mr-2 w-4 h-4 align-middle"></i>
              Einkaufsguide
            </Link>

            <div className="border-t border-foreground-100/30 my-1"></div>

            <button
              onClick={() => setMobileSubOpen(!mobileSubOpen)}
              className="w-full flex items-center justify-between px-4 py-3 text-sm font-semibold text-foreground-900 hover:bg-primary-50 transition-colors cursor-pointer whitespace-nowrap"
            >
              <span><i className="ri-price-tag-3-line inline mr-2 w-4 h-4 align-middle"></i>{t("nav_categories")}</span>
              <i className={`ri-arrow-down-s-line transition-transform duration-300 ${mobileSubOpen ? "rotate-180" : ""} w-4 h-4 flex items-center justify-center`}></i>
            </button>

            {mobileSubOpen && (
              <div className="bg-background-100 rounded-lg mx-2 mb-1 py-1">
                {categories.map((cat) => (
                  <button
                    key={cat.nameKey}
                    onClick={() => handleCategorySelect(cat.url)}
                    className="w-full text-left px-4 py-2.5 text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-700 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    {t(cat.nameKey)}
                  </button>
                ))}
              </div>
            )}

            <div className="border-t border-foreground-100/30 my-1"></div>

            <button onClick={() => scrollToSection("guides")} className={mobileLinkClass}>
              <i className="ri-file-text-line inline mr-2 w-4 h-4 align-middle"></i>
              {t("nav_guides")}
            </button>
            <button onClick={() => scrollToSection("comparison")} className={mobileLinkClass}>
              <i className="ri-scales-line inline mr-2 w-4 h-4 align-middle"></i>
              {t("nav_comparison")}
            </button>
            <button onClick={() => scrollToSection("security")} className={mobileLinkClass}>
              <i className="ri-shield-check-line inline mr-2 w-4 h-4 align-middle"></i>
              {t("nav_security")}
            </button>

            <div className="border-t border-foreground-100/30 my-1"></div>

            <div className="px-4 py-3 flex flex-col gap-2">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-primary-500 text-white font-semibold rounded-full text-sm hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                {t("hero_cta")}
              </a>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center justify-center gap-2 px-4 py-2.5 border border-foreground-300 text-foreground-800 font-semibold rounded-full text-sm hover:bg-foreground-50 transition-colors cursor-pointer whitespace-nowrap"
              >
                {t("cta_button")}
              </a>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}